import React, { FunctionComponent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldProps, useField, useFormikContext } from 'formik';
import moment, { Moment } from 'moment';
import styled from 'styled-components';
import { Grid, Label, Option } from '@inplayer-org/inplayer-ui';
import { MdInfoOutline } from 'react-icons/md';

// utils
import { capitalizeFirstChar } from 'utils/helpers';

// constants
import Rules from 'restrictions/roles/constants/rules';
import { OvpTypes } from 'pages/MerchantPages/Assets/InitialCreate/OvpVideoType';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// selectors
import { getItemDetails } from 'ducks/merchantDucks/items/selectors';

// types
import { EventType } from 'pages/MerchantPages/Assets/InitialCreate/ContentType';
import { FormValues } from 'pages/MerchantPages/Assets/ContentDetails/containers/AssetContentDetails';

// styled-compoentns
import { TooltipStyled } from 'pages/MerchantPages/Assets/Vouchers/shared/sharedComponents';
import { ItalicDescription, Description } from 'components/SharedStyledElements';

// components
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import Select from 'components/Fields/Select';
import TextEditor from 'components/TextEditor';
import CountCharactersModule from 'components/CharacterCounterModule';
import CheckboxField from 'components/Fields/CheckboxField';

const { Cell, Container } = Grid;

const FlexCell = styled(Cell)`
  display: flex;
  flex-direction: column;
  grid-gap: 1rem;
`;

const StyledLabel = styled(Label)`
  margin: 0.3rem 0 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  margin-top: 1rem;
  margin-bottom: 0.5rem;

  a {
    margin-left: 0.3rem;
    line-height: 1rem;
  }
`;

enum Industries {
  BUSINESS = 'Business',
  EDUCATION = 'Education & Training',
  FAITH = 'Faith',
  FITNESS = 'Fitness & Wellness',
  MUSIC = 'Music & Entertainment',
  SPORTS = 'Sports',
}

interface DateTimeFieldProps {
  name: string;
}

interface EnhancedOption extends Option {
  disabledFor?: Array<OvpTypes>;
}

const industryOptions: Array<Option> = [
  { displayName: Industries.BUSINESS, value: Industries.BUSINESS, tag: Industries.BUSINESS },
  { displayName: Industries.EDUCATION, value: Industries.EDUCATION, tag: Industries.EDUCATION },
  { displayName: Industries.FAITH, value: Industries.FAITH, tag: Industries.FAITH },
  { displayName: Industries.FITNESS, value: Industries.FITNESS, tag: Industries.FITNESS },
  { displayName: Industries.MUSIC, value: Industries.MUSIC, tag: Industries.MUSIC },
  { displayName: Industries.SPORTS, value: Industries.SPORTS, tag: Industries.SPORTS },
];

const contentCategoryOptions: Array<EnhancedOption> = [
  {
    displayName: capitalizeFirstChar(EventType.LIVE),
    value: EventType.LIVE,
  },
  {
    displayName: EventType.VOD.toUpperCase(),
    value: EventType.VOD,
    disabledFor: [OvpTypes.IVS],
  },
  {
    displayName: capitalizeFirstChar(EventType.OTHER),
    value: EventType.OTHER,
    disabledFor: [OvpTypes.IVS],
  },
];

export const replayAvailableOption: Array<Option> = [
  { displayName: 'yes', value: 'true', tag: 'true' },
  { displayName: 'no', value: 'false', tag: 'false' },
];

const maxCharacters = 2001;
const REFERENCE_DATE = moment('2021-06-16').endOf('day');
const dateFormat = 'MMMM DD YYYY';

type Props = {
  values: FormValues;
  handleSelectContentCategory: (category: string) => void;
  isTemplate?: boolean;
};

const ContentTypeDetails: FunctionComponent<Props> = ({
  values,
  handleSelectContentCategory,
  isTemplate = false,
}: Props) => {
  const { validateField, setFieldValue, setFieldTouched } = useFormikContext();
  const { contentCategory, industry, replayAvailable, isAdvertised } = values;

  const {
    created_at: itemCreatedAt,
    event_type: eventType,
    item_type: { host: ovpType },
  } = useSelector(getItemDetails);

  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  const handleDateTimeChange = (fieldName: string) => async (datetime: Moment) => {
    await setFieldValue(fieldName, datetime);
    await setFieldTouched(fieldName, true);
    validateField(fieldName);
  };

  const DateTimeField = (props: DateTimeFieldProps) => {
    const [field] = useField(props);
    const { value: fieldValue } = field;
    const { name: fieldName } = props;

    return (
      <>
        <Cell width={6} middle>
          <SingleDayPicker
            date={fieldValue}
            handleDateChange={handleDateTimeChange(fieldName)}
            disabled={isReadOnly}
            disablePastDays
          />
        </Cell>
        <Cell width={4} middle>
          <TimeHourPicker
            handleTimeChange={handleDateTimeChange(fieldName)}
            time={fieldValue}
            placeholder="Time"
            disabled={isReadOnly}
          />
        </Cell>
      </>
    );
  };

  const renderLiveEventFields = () => (
    <>
      <Cell width={6}>
        <Field
          tag="dropdown_industry"
          name="industry"
          label="Industry"
          value={industry}
          options={industryOptions}
          defaultOption={{ displayName: 'Select industry' }}
          component={Select}
          disable={isReadOnly}
        />
      </Cell>
      <FlexCell width={6}>
        <>
          <Cell>
            <Field
              ag="text_event_name"
              name="eventName"
              label="Event name"
              type="text"
              component={Input}
              disabled={isReadOnly}
            />
            <FieldError name="eventName" />
          </Cell>
          <Cell>
            <Container>
              <Cell width={12}>
                <Label>Event webpage</Label>
                <TooltipStyled
                  tag="tooltip_event_webpage"
                  placement="right"
                  content={`Enter the webpage or webpages where your event\n will be available for watching.`}
                >
                  <MdInfoOutline />
                </TooltipStyled>
              </Cell>
              <Cell width={12}>
                <Field tag="text_webpage" name="webPage" type="text" component={Input} disabled={isReadOnly} />
                <FieldError name="webPage" />
              </Cell>
            </Container>
          </Cell>
        </>
      </FlexCell>
      <FlexCell width={6}>
        <Cell>
          <Container>
            <Cell width={12}>
              <StyledLabel>Date and time (in your local time)</StyledLabel>
              <TooltipStyled
                tag="tooltip_calendar_event_date_and_time"
                content={`Enter the date and time when the event will start.\n Please enter your local time.`}
              >
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={12}>
              <Container>
                <DateTimeField name="eventDate" />
                <FieldError name="eventDate" />
              </Container>
            </Cell>
          </Container>
        </Cell>
        <Cell>
          <Field
            tag="text_expected_viewership"
            name="expectedViewership"
            label="Expected viewership"
            type="text"
            component={Input}
            disabled={isReadOnly}
          />
          <FieldError name="expectedViewership" />
        </Cell>
      </FlexCell>
      <Cell width={6}>
        <Field
          tag="dropdown_replay"
          name="replayAvailable"
          label="Will a replay be available?"
          value={replayAvailable}
          options={replayAvailableOption}
          defaultOption={{ displayName: 'Select availability' }}
          component={Select}
          disabled={isReadOnly}
        />
        <FieldError name="replayAvailable" />
      </Cell>
      <Cell width={12}>
        <Field name="isAdvertised">
          {({ field, form }: any) => (
            <CheckboxContainer>
              <CheckboxField
                tag="checkbox_advertise"
                name="isAdvertised"
                id="isAdvertised"
                checked={field.value}
                label="Advertise my event on InPlayer’s"
                onCheck={(val) => form.setFieldValue('isAdvertised', val)}
                disabled={isReadOnly}
              />
              <a href="https://inplayer.com/live-events/" target="_blank" rel="noopener noreferrer">
                event page
              </a>
            </CheckboxContainer>
          )}
        </Field>
      </Cell>
      {isAdvertised && (
        <Cell width={12}>
          <Container>
            <Cell width={12}>
              <Label>Event description</Label>
              <TooltipStyled
                placement="right"
                content={`Enter the content for the event post that will be posted on InPlayer’s\n event page. The post will appear as soon as it is approved by our\n site moderators.`}
              >
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={12}>
              <Field name="eventDescription">
                {({ field: { value, onChange, name } }: FieldProps) => (
                  <>
                    <TextEditor
                      value={value}
                      hideToolbarLink
                      readOnly={isReadOnly}
                      onChange={(val: string) => {
                        // set empty string as a value when the input in
                        // ReactQuill Textarea is removed (the remaining
                        // value is <p><br><p>)
                        const editorVal = val === '<p><br></p>' ? '' : val;
                        onChange(name)(editorVal);
                      }}
                      customModules={{
                        counter: {
                          container: '#counter',
                          unit: 'character',
                        },
                        maxlength: { maxCharacters },
                      }}
                    />
                    <CountCharactersModule maxCharacters={maxCharacters} />
                  </>
                )}
              </Field>
              <FieldError name="eventDescription" />
            </Cell>
          </Container>
        </Cell>
      )}
    </>
  );

  const filteredCategoryOptions = useMemo(
    () => contentCategoryOptions.filter(({ disabledFor }) => !disabledFor?.includes(ovpType as OvpTypes)),
    [eventType]
  );

  const renderTypeEventFields = () => {
    const showExplainContentCategoryMessage =
      eventType === EventType.OTHER && moment(itemCreatedAt * 1000).isSameOrBefore(REFERENCE_DATE);

    return (
      <StyledContainer rowGap="1rem" columns={12}>
        <Cell width={6}>
          <Container>
            <Cell width={12}>
              <Label>Content category</Label>
              <TooltipStyled
                tag="tooltip_content_category"
                placement="right"
                content={`Choose Live for live streamed video content. This includes live\n streams where you plan on publishing a replay afterwards.\n Choose VOD for Video-on-demand-only content that isn’t going\n to be live streamed.\nChoose Other for content other than video, or for test assets.`}
              >
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={12}>
              <Field
                tag="dropdown_content_category"
                name="contentCategory"
                value={contentCategory}
                options={filteredCategoryOptions}
                component={Select}
                disabled={isReadOnly}
                handleChange={handleSelectContentCategory}
              />
            </Cell>
          </Container>
        </Cell>
        {showExplainContentCategoryMessage && (
          <Cell width={12}>
            <ItalicDescription variant="p" margin="1rem 0">
              {`*All assets created before ${REFERENCE_DATE.format(
                dateFormat
              )} have been set as "Other" by default. When creating new assets, make
          sure to select the correct Content category, depending on your asset.`}
            </ItalicDescription>
          </Cell>
        )}
        {contentCategory === EventType.LIVE && renderLiveEventFields()}
      </StyledContainer>
    );
  };

  return (
    <Container columns={4} rowGap="2rem">
      <Cell width={4}>
        <Description variant="p">
          {`Make sure your customers get the best support by filling in the details below.\nThis is for internal info purposes only and won't affect your ${
            isTemplate ? 'asset template' : 'asset'
          } settings.`}
        </Description>
      </Cell>
      <Cell width={4}>{renderTypeEventFields()}</Cell>
    </Container>
  );
};

export default ContentTypeDetails;
